'use client'

import { useQueries } from '@tanstack/react-query'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { selectAtom } from 'jotai/utils'
import { PlusIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Fragment, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useWatch } from 'react-hook-form'

import { InfoTip } from '@/components/ui/info-tip'
import { Input } from '@/components/ui/input'
import { AdvancedTwapParams } from '@/features/advanced-twap/components/advanced-twap-params'
import {
  formatCompactNumber,
  getPrecisionFromStep,
  parseFormattedNumber,
} from '@/features/order/lib/number-format-util'
import { OrderFormEmptyState } from '@/features/order/order-form/components/order-form-empty-state'
import { ExchangeAccountTabBar } from '@/features/order/order-form/components/widget/exchange-account-tab-bar'
import { TickerInfo } from '@/features/order/order-form/components/widget/input-with-ticker-list'
import type {
  OffsetUnit,
  PriceAnchor,
} from '@/features/order/order-form/components/widget/limit-price-field'
import { LimitPriceField } from '@/features/order/order-form/components/widget/limit-price-field'
import { OrderExecutionParams } from '@/features/order/order-form/components/widget/order-execution-params'
import {
  FormInput,
  FormSection,
} from '@/features/order/order-form/components/widget/order-form-fields'
import { OrderRow } from '@/features/order/order-form/components/widget/order-row'
import { PromptFillFlash } from '@/features/order/order-form/components/widget/prompt-fill-flash'
import { TwapIntervalInput } from '@/features/order/order-form/components/widget/twap-interval-input'
import { useOrderFormTradeVenue } from '@/features/order/order-form/hooks/use-order-form-trade-venue'
import { splitMaxSize } from '@/features/order/order-form/lib/format-order-preview'
import { getInstrumentUnifiedKind } from '@/features/order/order-form/lib/instrument-filter'
import {
  resolveBaseCurrency,
  resolveDenomination,
  resolveQuoteCurrency,
} from '@/features/order/order-form/lib/instrument-resolution'
import { lockAfterEdit } from '@/features/order/order-form/lib/locked-amount'
import {
  computeMinNotionalByIndex,
  effectiveAmountUnit,
  resolveRefPrice,
} from '@/features/order/order-form/lib/min-notional'
import { orderFormMinNotionalAtomFamily } from '@/features/order/order-form/lib/min-notional-atom'
import { useUnifiedOrderFormContext } from '@/features/order/order-form/lib/order-form-context'
import { resolveOptionInstrument } from '@/features/order/order-form/lib/parse-option-instrument'
import { getRowGroupKey } from '@/features/order/order-form/lib/row-group-key'
import { RfqParams } from '@/features/rfq/components/rfq-params'
import { ScaleParams } from '@/features/scale/components/scale-params'
import { SpreaderTickerPicker } from '@/features/spreader/components/spreader-ticker-picker'
import { useNumberFormat, useNumberInput } from '@/hooks/use-number-format'
import { isInverseInstrument } from '@/lib/instrument-util'
import { getPositionRowKey } from '@/lib/position-row-key'
import { parseExchangeField, toExchangeEnum, tradeVenueToExchangeKey } from '@/lib/proto/util'
import { safeParseFloat } from '@/lib/utils'
import { resolveQtyStep } from '@/lib/utils/qty-precision'
import { useAccountPrimaryExchange } from '@/service/account-exchanges.hook'
import { instrumentListQueryOptions, useInstrumentList } from '@/service/instrument.hook'
import { useMultiTickerStream } from '@/service/ws/use-ticker-stream'
import { allBalancesAtom, allPositionsAtom } from '@/state/account'
import {
  findTickerBySymbol,
  symbolFromTickerKey,
  type WatchListTickerMap,
  watchListTickerMapAtom,
} from '@/state/dashboard'
import {
  markOrderFormManual,
  orderFormDenomPreferenceAtomFamily,
  orderFormSelectionAtomFamily,
  OrderFormSummaryTab,
  orderFormSummaryTabAtomFamily,
} from '@/state/order'
import { SpreaderMatrixInstrument } from '@/state/widget-instances'
import { SelectedOption } from '@/types/dashboard'
import { Denomination } from '@/types/generated/common/trading'
import {
  Exchange,
  Instrument,
  InstrumentKind,
  MarketDataType,
} from '@/types/generated/common/types'
import { BatchOrderPreviewResponse } from '@/types/generated/public_api'

// Helper to get USD price for a quote currency from ticker map
// Returns null if USD price cannot be determined
function getQuotePriceInUSD(
  quoteCurrency: string,
  tickerMap: Record<string, { lastPrice?: string }>
): number | null {
  // USD and USD-pegged stablecoins
  if (['USD', 'USDC', 'USDT'].includes(quoteCurrency.toUpperCase())) {
    return 1
  }

  // Look for the quote currency's own USD price in ticker map
  // Search for any symbol that matches pattern: {quoteCurrency}-* or {quoteCurrency}_*
  for (const [key, data] of Object.entries(tickerMap)) {
    if (!data?.lastPrice) continue
    const symbol = symbolFromTickerKey(key)
    // Found quote currency as base with USD quote
    if (
      resolveBaseCurrency([], symbol) === quoteCurrency &&
      resolveQuoteCurrency([], symbol) === 'USD'
    ) {
      return safeParseFloat(data.lastPrice) || null
    }
  }

  // Cannot determine USD price
  return null
}

interface UnifiedOrderFormProps {
  // Combined preview across every row (Task 17). Owned by order-form-summary
  // (it also needs instrumentListData/accountOverrides to build the request),
  // so it's threaded in as a prop rather than duplicated here.
  batchPreview?: BatchOrderPreviewResponse
  // Maps a field's index (in `fields`/`items`) to its row index in
  // batchPreview.rows — undefined if that row was dropped from the batch
  // request (mid-typing) or the two would silently misalign whenever an
  // earlier row is incomplete. See mapItemsToBatchRowIndex.
  batchRowIndexByItem?: (number | undefined)[]
  // Fat-finger warnings for the limit price, rendered right under the price field. Computed by
  // order-form-summary, which owns the live per-leg book and gates submit on the same data.
  priceWarnings?: ReactNode
}

function TwapDurationField({ label }: { label: string }) {
  const { form } = useUnifiedOrderFormContext()
  const value = useWatch({ control: form.control, name: 'twapIntervalMinutes' })

  return (
    <FormSection label={label}>
      <TwapIntervalInput
        value={value || ''}
        onChange={(next) => form.setValue('twapIntervalMinutes', next)}
      />
    </FormSection>
  )
}

function SpreaderParams() {
  const t = useTranslations('orderForm')
  const { form } = useUnifiedOrderFormContext()
  const [slicePct, limitPrice] = useWatch({
    control: form.control,
    name: ['spreaderSlicePct', 'spreaderLimitPrice'],
  })
  const slicePctInput = useNumberInput(slicePct || '')
  const limitPriceInput = useNumberInput(limitPrice || '')

  return (
    <div data-tutorial="slice" className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <FormSection label={t('spreaderSliceSize')}>
        <FormInput
          aria-label={t('spreaderSliceSize')}
          unit="%"
          {...slicePctInput.bind((v) =>
            form.setValue('spreaderSlicePct', v, { shouldDirty: true })
          )}
        />
      </FormSection>
      <FormSection label={t('spreaderLimitPrice')} info={t('spreaderLimitPriceHint')}>
        <FormInput
          aria-label={t('spreaderLimitPrice')}
          placeholder={t('unbounded')}
          {...limitPriceInput.bind((v) =>
            form.setValue('spreaderLimitPrice', v, { shouldDirty: true })
          )}
        />
      </FormSection>
    </div>
  )
}

/**
 * Unified Order Form - handles all order types:
 * - General (Spot/Perp/Future) - single or multi instruments
 * - Single-leg options
 * - Multi-leg options (TWAP)
 * - TWAP for any instrument
 */
export default function UnifiedOrderForm({
  batchPreview,
  batchRowIndexByItem,
  priceWarnings,
}: UnifiedOrderFormProps) {
  const t = useTranslations('orderForm')
  const fmt = useNumberFormat()
  const formRootRef = useRef<HTMLDivElement>(null)
  const focusedEmptyAmountKeyRef = useRef<string | null>(null)
  // True only while the current 'mid' anchor was set BY the multi-instrument
  // auto-switch below, never by the user's own pick — lets that effect's
  // reverse direction (back to single row) restore 'price' without also
  // clobbering an anchor the user explicitly chose while multi-row.
  const autoSwitchedAnchorRef = useRef(false)
  const formContext = useUnifiedOrderFormContext()
  const {
    mode,
    isTwapMode,
    isAdvancedTwapMode,
    isScaleMode,
    isLimitChaseMode,
    isMarketOrder,
    isSpreaderMode,
    isSingleVisualRow,
    form,
    fields,
    orderAccounts,
    visibleItemIndexes,
    handleAmountChangeForGroup,
    handleSideChange,
    handleSpreaderRoleChange,
    addSpreaderLeg,
    setOrderAccountFilter,
    resolveInheritedAccountId,
    resolveItemAccountId,
    markPriceSynced,
    syncPriceFromAtm,
  } = formContext

  const selectedTab = useAtomValue(orderFormSummaryTabAtomFamily(formContext.cardId))
  const isRfqMode = selectedTab === OrderFormSummaryTab.RFQ
  const denomPreference = useAtomValue(orderFormDenomPreferenceAtomFamily(formContext.cardId))
  const cardTradeVenue = useOrderFormTradeVenue(formContext.cardId)
  const primaryExchange = useAccountPrimaryExchange(cardTradeVenue)
  const { data: instrumentListData } = useInstrumentList(primaryExchange)
  // Narrowed positions/balances subscription — same reasoning as tickerSliceAtom below:
  // allPositionsAtom/allBalancesAtom hold every account's live data and get a new
  // top-level array identity on every WS tick to ANY account, but merge-positions.ts/
  // mergePortfolioData keep an untouched entry's object reference stable across merges.
  // Slicing to just this form's own accounts and comparing per-item by reference means
  // the "Current"/"Max" size hints below only actually re-render when one of THOSE
  // accounts' data changes, not on every other account's tick elsewhere in the app.
  const formAccountIds = useMemo(
    () => new Set(fields.map((f) => f.accountId).filter((id): id is string => Boolean(id))),
    [fields]
  )
  const positionsSliceAtom = useMemo(
    () =>
      selectAtom(
        allPositionsAtom,
        (all) => all.filter((p) => formAccountIds.has(p.accountId)),
        (a, b) => a.length === b.length && a.every((p, i) => p === b[i])
      ),
    [formAccountIds]
  )
  const balancesSliceAtom = useMemo(
    () =>
      selectAtom(
        allBalancesAtom,
        (all) => all.filter((b) => formAccountIds.has(b.accountId)),
        (a, b) => a.length === b.length && a.every((p, i) => p === b[i])
      ),
    [formAccountIds]
  )
  const positions = useAtomValue(positionsSliceAtom)
  const balances = useAtomValue(balancesSliceAtom)
  const positionsByRowKey = useMemo(
    () =>
      new Map(
        positions.map((position) => [
          getPositionRowKey(position.accountId, position.exchange, position.symbol),
          position,
        ])
      ),
    [positions]
  )
  const balancesByAccountCurrency = useMemo(() => {
    const result = new Map<string, number>()
    for (const balance of balances) {
      const amount = safeParseFloat(balance.equity)
      if (!Number.isFinite(amount)) continue
      const key = `${balance.accountId}:${balance.currency}`
      result.set(key, (result.get(key) ?? 0) + amount)
    }
    return result
  }, [balances])
  // Single-item price-preview scope below only ever looks at one field (isSingleItem),
  // so the account's own primary-exchange instrument list is the right source — same
  // scope instrumentListData above already fetches, just reused as an array/map.
  const instruments = useMemo(() => instrumentListData?.instruments ?? [], [instrumentListData])
  const instrumentMap = useMemo(() => new Map(instruments.map((i) => [i.symbol, i])), [instruments])

  // Narrowed tickerMap subscription: the form only needs the leg symbols' own
  // ticker entries plus a quote→USD conversion per leg quote currency, but the
  // whole-map atom changes identity on every WS tick of every streamed symbol —
  // subscribing to it re-rendered this entire form on unrelated ticks. The
  // selectAtom slice re-renders only when a leg's entry (or a quote-USD price)
  // actually changes.
  const legSymbols = useMemo(
    () => [...new Set(fields.map((f) => f.symbol).filter(Boolean))],
    [fields]
  )
  // Union of both quote-currency variants used at render time (field override
  // and instrument-map resolution) so every lookup key below is precomputed.
  const legQuoteCurrencies = useMemo(() => {
    const currencies = new Set<string>()
    for (const field of fields) {
      if (!field.symbol) continue
      if (field.quoteCurrency) currencies.add(field.quoteCurrency)
      currencies.add(resolveQuoteCurrency(instrumentMap, field.symbol))
    }
    return [...currencies]
  }, [fields, instrumentMap])
  const tickerSliceAtom = useMemo(
    () =>
      selectAtom(
        watchListTickerMapAtom,
        (map) => {
          const entries: Record<string, WatchListTickerMap[string] | undefined> = {}
          for (const symbol of legSymbols) entries[symbol] = findTickerBySymbol(map, symbol)
          const quoteUsd: Record<string, number | null> = {}
          for (const quote of legQuoteCurrencies) quoteUsd[quote] = getQuotePriceInUSD(quote, map)
          return { entries, quoteUsd }
        },
        (a, b) =>
          legSymbols.every((symbol) => a.entries[symbol] === b.entries[symbol]) &&
          legQuoteCurrencies.every((quote) => a.quoteUsd[quote] === b.quoteUsd[quote])
      ),
    [legSymbols, legQuoteCurrencies]
  )
  const { entries: legTickerMap, quoteUsd: legQuoteUsd } = useAtomValue(tickerSliceAtom)

  // watchListTickerMapAtom only holds symbols some OTHER widget streams (a visible watchlist
  // row, a chart tab), so with neither open a leg had no price at all — and the base/quote
  // toggle converted at a made-up 1 ("$10" became "10 BTC"). Stream the legs here too, as
  // order-form-summary.tsx does for its preview. Used only where the shared map has nothing,
  // and written at most once a second per symbol so ticks don't re-render the whole form.
  const legStreamPayloads = useMemo(() => {
    const seen = new Set<string>()
    return fields.flatMap((field) => {
      if (!field.symbol) return []
      const exchange = parseExchangeField(field.exchange, primaryExchange)
      const key = `${exchange}:${field.symbol}`
      if (seen.has(key)) return []
      seen.add(key)
      return [{ symbol: field.symbol, exchange, dataType: MarketDataType.MARKET_DATA_TYPE_TICKER }]
    })
  }, [fields, primaryExchange])
  const [streamedLegPrice, setStreamedLegPrice] = useState<Record<string, string>>({})
  const streamedAtRef = useRef<Record<string, number>>({})
  useMultiTickerStream({
    connected: legStreamPayloads.length > 0,
    payloads: legStreamPayloads,
    onMessage: (data) => {
      const now = Date.now()
      const updates: Record<string, string> = {}
      for (const ticker of data?.tickers ?? []) {
        const price = ticker.lastPrice || ticker.markPrice
        if (!ticker.symbol || !price || !(Number(price) > 0)) continue
        if (now - (streamedAtRef.current[ticker.symbol] ?? 0) < 1000) continue
        streamedAtRef.current[ticker.symbol] = now
        updates[ticker.symbol] = price
      }
      if (Object.keys(updates).length > 0) {
        setStreamedLegPrice((prev) => ({ ...prev, ...updates }))
      }
    },
  })
  const visibleIndexSet = useMemo(() => new Set(visibleItemIndexes), [visibleItemIndexes])
  const accountsByExchange = useMemo(() => {
    const grouped = new Map<Exchange, typeof orderAccounts>()
    for (const account of orderAccounts) {
      const exchange = toExchangeEnum(tradeVenueToExchangeKey(account.tradeVenue))
      const accounts = grouped.get(exchange) ?? []
      accounts.push(account)
      grouped.set(exchange, accounts)
    }
    return grouped
  }, [orderAccounts])
  // Groups items sharing (exchange, accountId, itemType, symbol) — see
  // row-group-key.ts — into one visual row. Different accounts on the same
  // venue deliberately remain separate cards. `field`/`index` stay the GROUP's representative
  // (first member) so every existing per-symbol computation below (ticker
  // data, qtyStep, baseTicker, etc. — all symbol/exchange-scoped, identical
  // across every member of a group by construction) keeps working unchanged;
  // `memberIndexes` is the full set, for the group-aware handlers
  // (handleAmountChangeForGroup, handleSymbolChangeForGroup,
  // handleRemoveGroup) that must apply to every account in a split row.
  // First-seen order is preserved within each exchange/account section.
  const groupedVisibleFields = useMemo(() => {
    const visible = fields.flatMap((field, index) =>
      visibleIndexSet.has(index) ? [{ field, index }] : []
    )
    // Sections (one per exchange+account) are ordered by when that section
    // FIRST appeared in `fields` — i.e. add order — not by exchange
    // enum/accountId string. `fields` itself already appends newly-added
    // rows at the end (see the sync effect in use-unified-order-form.ts), so
    // a leg added on a new exchange/account must land as a new section at
    // the bottom instead of sorting into the middle by enum/id value.
    const sectionKeyOf = (field: (typeof fields)[number]) =>
      `${parseExchangeField(field.exchange, primaryExchange)}:${field.accountId ?? ''}`
    const sectionFirstIndex = new Map<string, number>()
    for (const { field, index } of visible) {
      const key = sectionKeyOf(field)
      if (!sectionFirstIndex.has(key)) sectionFirstIndex.set(key, index)
    }
    const sorted = visible.toSorted((a, b) => {
      const firstA = sectionFirstIndex.get(sectionKeyOf(a.field))!
      const firstB = sectionFirstIndex.get(sectionKeyOf(b.field))!
      return firstA - firstB || a.index - b.index
    })
    const groups = new Map<
      string,
      { field: (typeof fields)[number]; index: number; memberIndexes: number[] }
    >()
    const order: string[] = []
    for (const { field, index } of sorted) {
      const key = getRowGroupKey(field, primaryExchange)
      const existing = groups.get(key)
      if (existing) {
        existing.memberIndexes.push(index)
      } else {
        groups.set(key, { field, index, memberIndexes: [index] })
        order.push(key)
      }
    }
    return order.map((key) => groups.get(key)!)
  }, [fields, primaryExchange, visibleIndexSet])

  // Per-field-exchange instrument lookup: tick size / qty precision must come
  // from EACH ROW'S OWN exchange (same fix as base/quote/kind in OrderRow),
  // not `primaryExchange` (the active account's, which `instrumentListData`
  // above is scoped to) — otherwise a row on a different exchange than the
  // active account gets a wrong price/amount step even once its quote/kind
  // display correctly. `useQueries` (not multiple useInstrumentList calls)
  // because the set of distinct exchanges across `fields` is dynamic — can't
  // call a hook a variable number of times. Uses `instrumentListQueryOptions`
  // (same options useInstrumentList itself uses) so this shares React Query's
  // cache AND filtering (e.g. btcdvol exclusion) with OrderRow's own per-row
  // fetch — no duplicate requests, no risk of the two paths drifting apart.
  const fieldExchanges = useMemo(
    () => [...new Set(fields.map((f) => parseExchangeField(f.exchange, primaryExchange)))],
    [fields, primaryExchange]
  )

  // Spreader "+ Add leg": every leg already on the ticket, so the shared ticker picker can
  // filter them out (an instrument can't be added twice as two separate legs).
  const spreaderLegInstruments: SpreaderMatrixInstrument[] = useMemo(
    () =>
      fields.flatMap((field) =>
        field.symbol
          ? [
              {
                exchange: parseExchangeField(field.exchange, primaryExchange),
                symbol: field.symbol,
              },
            ]
          : []
      ),
    [fields, primaryExchange]
  )
  const spreaderPreferredBaseCurrency = useMemo(() => {
    const anchor = fields.find((field) => field.spreaderRole === 'quoting') ?? fields[0]
    if (!anchor?.symbol) return undefined
    return anchor.baseCurrency || resolveBaseCurrency([], anchor.symbol)
  }, [fields])
  const fieldExchangeInstrumentQueries = useQueries({
    queries: fieldExchanges.map((ex) => instrumentListQueryOptions(ex)),
  })
  // useQueries returns a brand-new result array every render (TanStack Query
  // doesn't memoize it by identity, only by internal observer state), so using
  // it directly as a dependency reruns this memo — and everything downstream
  // that depends on `getFieldInstrument` — on every render, not just when
  // instrument data actually changes. A render loop that keeps calling setValue
  // even once per pass (e.g. while data briefly disagrees across two fetches)
  // never gets a chance to settle when it's also re-triggered by unrelated
  // renders like every keystroke. Depend on each query's own `dataUpdatedAt`
  // instead — a primitive that only changes when the data itself does.
  const fieldInstrumentDataKey = fieldExchangeInstrumentQueries
    .map((q, i) => `${fieldExchanges[i]}:${q.dataUpdatedAt}`)
    .join('|')
  // Keyed by `exchange:symbol`, not just `symbol` — the whole point of this
  // per-field fetch is that different rows can be on different exchanges, so
  // merging by symbol alone would let a same-named instrument on one exchange
  // silently supply another exchange's row with the wrong tick size/qty
  // precision (first-fetched-exchange-wins), defeating the fix.
  const instrumentByFieldExchangeSymbol = useMemo(() => {
    const map = new Map<string, Instrument>()
    fieldExchangeInstrumentQueries.forEach((q, i) => {
      const ex = fieldExchanges[i]
      q.data?.instruments.forEach((inst) => {
        map.set(`${ex}:${inst.symbol}`, inst)
      })
    })
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldInstrumentDataKey, fieldExchanges])
  const getFieldInstrument = useCallback(
    (field: { symbol: string; exchange?: string }) =>
      instrumentByFieldExchangeSymbol.get(
        `${parseExchangeField(field.exchange, primaryExchange)}:${field.symbol}`
      ),
    [instrumentByFieldExchangeSymbol, primaryExchange]
  )
  // One derivation of a row's effective ticker pair, price and default unit, shared by the row
  // render and the min-notional check so the message and the submit block cannot disagree.
  const resolveRowUnits = useCallback(
    (field: (typeof fields)[number]) => {
      const fieldInstrument = getFieldInstrument(field)
      const isOption = field.itemType === 'option'
      const baseTicker = isOption
        ? field.baseCurrency || resolveBaseCurrency(instrumentMap, field.symbol, fieldInstrument)
        : resolveBaseCurrency(instrumentMap, field.symbol, fieldInstrument)
      const quoteCurrency = isOption
        ? field.quoteCurrency || resolveQuoteCurrency(instrumentMap, field.symbol, fieldInstrument)
        : resolveQuoteCurrency(instrumentMap, field.symbol, fieldInstrument)
      const lastPrice = resolveRefPrice({
        watchlist: legTickerMap[field.symbol]?.lastPrice,
        streamed: streamedLegPrice[field.symbol],
        itemPrice: field.lastPrice,
      })
      // Default denomination driven by user preference:
      // 'quote' → always quote, 'base' → always base, 'auto' → inverse=quote / linear=base
      const defaultDenomination =
        denomPreference === 'quote'
          ? quoteCurrency
          : denomPreference === 'base'
            ? baseTicker
            : isInverseInstrument(field.symbol, fieldInstrument)
              ? quoteCurrency
              : baseTicker
      return { fieldInstrument, baseTicker, quoteCurrency, lastPrice, defaultDenomination }
    },
    [getFieldInstrument, instrumentMap, legTickerMap, streamedLegPrice, denomPreference]
  )
  // useWatch, not form.watch: it emits a fresh value per edit, so the memo below recomputes as the
  // user types or switches the unit.
  const watchedItems = useWatch({ control: form.control, name: 'items' })
  const watchedReduceOnly = form.watch('reduceOnly') ?? false
  const minNotionalByIndex = useMemo(
    () =>
      computeMinNotionalByIndex(
        fields.map((field, index) => {
          const u = resolveRowUnits(field)
          return {
            item: {
              itemType: field.itemType,
              amount: watchedItems?.[index]?.amount,
              lockedAmount: (watchedItems?.[index] ?? field).lockedAmount,
            },
            instrument: u.fieldInstrument,
            quoteCurrency: u.quoteCurrency,
            amountUnit: effectiveAmountUnit(
              watchedItems?.[index]?.amountUnit || undefined,
              [u.quoteCurrency, u.baseTicker],
              u.defaultDenomination
            ),
            refPrice: u.lastPrice,
          }
        }),
        watchedReduceOnly
      ),
    [fields, resolveRowUnits, watchedItems, watchedReduceOnly]
  )
  const setMinNotional = useSetAtom(orderFormMinNotionalAtomFamily(formContext.cardId))
  useEffect(() => {
    setMinNotional(minNotionalByIndex)
    return () => setMinNotional({})
  }, [minNotionalByIndex, setMinNotional])

  const [selection, setSelection] = useAtom(orderFormSelectionAtomFamily(formContext.cardId))
  const { selectedOptions, selectedInstruments } = selection

  // Direct match by (symbol, accountId), NOT occurrence-index — occurrence
  // counting assumes `fields` and selectedOptions/selectedInstruments stay in
  // the same relative order for same-symbol entries, which isn't reliable
  // once accountId disambiguates two legs of a split multi-account row (see
  // row-group-key.ts). `index`'s own `field` argument already carries the
  // accountId to match on.
  const getSelectedOptionIndex = useCallback(
    (_index: number, field: (typeof fields)[number]) => {
      if (field.itemType !== 'option') return -1
      return selectedOptions.findIndex(
        (opt) =>
          opt.instrumentName === field.symbol && (opt.accountId ?? '') === (field.accountId ?? '')
      )
    },
    [selectedOptions]
  )

  const getSelectedInstrumentIndex = useCallback(
    (_index: number, field: (typeof fields)[number]) => {
      if (field.itemType !== 'instrument') return -1
      return selectedInstruments.findIndex(
        (inst) => inst.symbol === field.symbol && (inst.accountId ?? '') === (field.accountId ?? '')
      )
    },
    [selectedInstruments]
  )

  // Remove a field by index.
  // Scope: only toggles selectedOptions / selectedInstruments (order form rows).
  // watchList, selectedTicker, and chart state are NOT touched —
  // those belong to the watch-list row/tab management layer.
  const handleRemove = useCallback(
    (index: number, field: (typeof fields)[number]) => {
      setSelection((prev) => {
        let nextOptions = prev.selectedOptions
        let nextInstruments = prev.selectedInstruments

        if (field.itemType === 'option') {
          const optionIndex = getSelectedOptionIndex(index, field)
          nextOptions = prev.selectedOptions.filter((_, i) => i !== optionIndex)
        } else {
          const instIndex = getSelectedInstrumentIndex(index, field)
          nextInstruments = prev.selectedInstruments.filter((_, i) => i !== instIndex)
        }

        return { ...prev, selectedOptions: nextOptions, selectedInstruments: nextInstruments }
      })
    },
    [getSelectedInstrumentIndex, getSelectedOptionIndex, setSelection]
  )

  // Removes an ENTIRE merged row — every member/account at once — for the
  // row's own delete ('x') button. Direct match by (symbol, accountId), same
  // reasoning as getSelectedOptionIndex/getSelectedInstrumentIndex above:
  // occurrence-index math isn't reliable once accountId disambiguates two
  // members sharing a symbol.
  const handleRemoveGroup = useCallback(
    (indexes: number[]) => {
      setSelection((prev) => {
        let nextOptions = prev.selectedOptions
        let nextInstruments = prev.selectedInstruments
        for (const memberIndex of indexes) {
          const memberField = fields[memberIndex]
          if (!memberField) continue
          if (memberField.itemType === 'option') {
            nextOptions = nextOptions.filter(
              (opt) =>
                !(
                  opt.instrumentName === memberField.symbol &&
                  (opt.accountId ?? '') === (memberField.accountId ?? '')
                )
            )
          } else {
            nextInstruments = nextInstruments.filter(
              (inst) =>
                !(
                  inst.symbol === memberField.symbol &&
                  (inst.accountId ?? '') === (memberField.accountId ?? '')
                )
            )
          }
        }
        return { ...prev, selectedOptions: nextOptions, selectedInstruments: nextInstruments }
      })
    },
    [fields, setSelection]
  )

  // Watch form values
  const price = form.watch('price')
  const priceAnchor = (form.watch('priceAnchor') ?? 'price') as PriceAnchor
  const priceOffsetUnit = (form.watch('priceOffsetUnit') ?? 'quote_value') as OffsetUnit

  // Visual-row-based, not raw fields.length — a ticker split across 2+
  // accounts is still one row (see row-group-key.ts / use-unified-order-form
  // .ts's isSingleVisualRow), so it must keep single-leg-only UI (absolute
  // price, quote-value denomination, etc.) available.
  const isSingleItem = isSingleVisualRow
  const isMultiLeg = mode === 'multi-leg'
  const firstEmptyAmountIndex = fields.findIndex(
    (_, index) => !(form.watch(`items.${index}.amount`) || '').trim()
  )
  const firstEmptyAmountKey =
    firstEmptyAmountIndex >= 0
      ? `${fields[firstEmptyAmountIndex]?.fieldId ?? firstEmptyAmountIndex}:${firstEmptyAmountIndex}`
      : null

  useEffect(() => {
    if (firstEmptyAmountIndex < 0 || !firstEmptyAmountKey) {
      focusedEmptyAmountKeyRef.current = null
      return
    }
    if (focusedEmptyAmountKeyRef.current === firstEmptyAmountKey) return

    const animationFrame = requestAnimationFrame(() => {
      // Typing into an amount advances firstEmptyAmountIndex to the next leg; never
      // yank focus away from a field the user is actively editing.
      const active = document.activeElement as HTMLElement | null
      if (
        active &&
        (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)
      )
        return

      const input = formRootRef.current?.querySelector<HTMLInputElement>(
        `input[data-order-amount-index="${firstEmptyAmountIndex}"]`
      )
      if (!input || input.disabled || input.value.trim()) return

      focusedEmptyAmountKeyRef.current = firstEmptyAmountKey
      // preventScroll: a plain focus() scrolls the page to the field — on a scrolled-down
      // desktop dashboard, every linked tab switch re-runs this and yanked it to the top.
      input.focus({ preventScroll: true })
      input.select()
    })

    return () => cancelAnimationFrame(animationFrame)
  }, [firstEmptyAmountIndex, firstEmptyAmountKey])

  // Auto-switch anchor to 'mid' when multi-instrument (Price anchor only valid for a single visual row).
  // Symmetric on the way back: once the selection returns to a single row, restore
  // 'price' — but only if THIS effect was the one that moved it to 'mid' in the first
  // place (autoSwitchedAnchorRef), never overriding an anchor the user picked
  // themselves. Without this reverse direction, picking one option/instrument after
  // a moment of multi-row selection (e.g. an option-chain click landing alongside an
  // existing row) left the anchor stuck on 'mid' for every later single-row pick too,
  // since nothing ever set it back to the 'price' default.
  useEffect(() => {
    if (!isSingleVisualRow && form.getValues('priceAnchor') === 'price') {
      form.setValue('priceAnchor', 'mid')
      form.setValue('price', '0')
      markPriceSynced()
      autoSwitchedAnchorRef.current = true
    } else if (isSingleVisualRow && autoSwitchedAnchorRef.current) {
      autoSwitchedAnchorRef.current = false
      if (form.getValues('priceAnchor') === 'mid') {
        form.setValue('priceAnchor', 'price')
        syncPriceFromAtm()
      }
    }
  }, [isSingleVisualRow, form, markPriceSynced, syncPriceFromAtm])

  // Auto-switch offsetUnit away from 'quote_value' when multi-instrument
  useEffect(() => {
    if (!isSingleVisualRow && form.getValues('priceOffsetUnit') === 'quote_value') {
      form.setValue('priceOffsetUnit', 'bps')
    }
  }, [isSingleVisualRow, form])

  // Quote currency label for LimitPriceField unit selector
  const quoteCurrencyLabel = useMemo(() => {
    if (fields.length !== 1) return 'Quote'
    const field = fields[0]
    return field.quoteCurrency || resolveQuoteCurrency([], field.symbol) || 'USD'
  }, [fields])

  // Calculate approximate size for display (single-item only)
  const approxSize = useMemo(() => {
    if (!isSingleItem || fields.length === 0) return null

    const field = fields[0]
    const itemAmount = form.watch('items.0.amount')
    const amountNum = parseFormattedNumber(itemAmount || '0')
    if (isNaN(amountNum) || amountNum <= 0) return null

    // Option-specific calculation
    if (field.itemType === 'option') {
      const option = selectedOptions.find((opt) => opt.instrumentName === field.symbol)
      if (!option?.ticker?.indexPrice) return null

      const indexPrice = safeParseFloat(option.ticker.indexPrice)
      if (!indexPrice || indexPrice <= 0) return null

      const tickerData = legTickerMap[field.symbol]
      const tickerPrice = tickerData?.lastPrice ? safeParseFloat(tickerData.lastPrice) : 0
      const currentPrice =
        !isMarketOrder && !isLimitChaseMode ? parseFormattedNumber(price || '0') : tickerPrice
      const optionPrice = currentPrice || tickerPrice

      if (optionPrice <= 0) return null

      const fieldInstrument = getFieldInstrument(field)
      const parsedOption = resolveOptionInstrument(field.symbol, fieldInstrument)
      const isUSDCQuoted = parsedOption?.isUSDCQuoted ?? false
      const baseTicker =
        parsedOption?.baseCurrency ||
        field.baseCurrency ||
        resolveBaseCurrency(instrumentMap, field.symbol, fieldInstrument)

      const contractSize = Math.max(1, Number(fieldInstrument?.contractSize) || 1)

      const btcValue = !isUSDCQuoted ? amountNum * contractSize * optionPrice : null
      const usdValue = isUSDCQuoted ? null : amountNum * contractSize * indexPrice * optionPrice
      const usdcValue = isUSDCQuoted ? amountNum * contractSize * optionPrice : null

      return {
        quote: amountNum,
        quoteTicker: 'contract',
        base: amountNum,
        baseTicker,
        usd: usdValue,
        btcValue,
        usdcValue,
        isUSDCQuoted,
        isBaseInput: true,
        isUSDQuote: !isUSDCQuoted,
      }
    }

    // Perpetual/Spot calculation
    const fieldInstrument = getFieldInstrument(field)
    const baseTicker =
      field.baseCurrency || resolveBaseCurrency(instrumentMap, field.symbol, fieldInstrument)
    const quoteCurrency =
      field.quoteCurrency || resolveQuoteCurrency(instrumentMap, field.symbol, fieldInstrument)

    const tickerData = legTickerMap[field.symbol]
    const tickerPrice = tickerData?.lastPrice ? safeParseFloat(tickerData.lastPrice) : 0

    const isRelativeAnchor = priceAnchor !== 'price'
    const currentPrice =
      isRelativeAnchor || isMarketOrder || isLimitChaseMode
        ? tickerPrice
        : parseFormattedNumber(price || '0')
    const effectivePrice = currentPrice || tickerPrice

    if (effectivePrice <= 0) return null

    const amountUnit = form.watch('items.0.amountUnit')
    const isBaseInput = amountUnit === baseTicker

    const quotePriceInUSD = legQuoteUsd[quoteCurrency] ?? null
    const isUSDQuote = ['USD', 'USDC', 'USDT'].includes(quoteCurrency.toUpperCase())

    if (isBaseInput) {
      const quoteValue = amountNum * effectivePrice
      const usdValue = quotePriceInUSD !== null ? quoteValue * quotePriceInUSD : null
      return {
        quote: quoteValue,
        quoteTicker: quoteCurrency,
        base: amountNum,
        baseTicker,
        usd: usdValue,
        isBaseInput: true,
        isUSDQuote,
      }
    } else {
      const baseValue = amountNum / effectivePrice
      const usdValue = quotePriceInUSD !== null ? amountNum * quotePriceInUSD : null
      return {
        quote: amountNum,
        quoteTicker: quoteCurrency,
        base: baseValue,
        baseTicker,
        usd: usdValue,
        isBaseInput: false,
        isUSDQuote,
      }
    }
  }, [
    isSingleItem,
    fields,
    legTickerMap,
    legQuoteUsd,
    isMarketOrder,
    isLimitChaseMode,
    priceAnchor,
    price,
    form,
    selectedOptions,
    instrumentMap,
    getFieldInstrument,
  ])

  // Display strings for the approx-size row. The sizes reveal the user's order, so they
  // go through the number formatter as sensitive (masked while balances are hidden).
  const { approxSizeText, approxSizeUsdSuffix } = useMemo(() => {
    if (!approxSize) return { approxSizeText: '', approxSizeUsdSuffix: null }
    const size = (value: number, options: Intl.NumberFormatOptions) =>
      fmt.number(value, { sensitive: true, ...options })
    const base = size(approxSize.base, { maximumFractionDigits: 6 })

    if (approxSize.quoteTicker === 'contract') {
      const contracts = t('approxSizeContracts', { count: base })
      // Quote option (e.g., ETH_USDC-4APR26-2050-C): price is in USDC → show USDC total.
      if (approxSize.isUSDCQuoted) {
        const usdc =
          approxSize.usdcValue !== null && approxSize.usdcValue !== undefined
            ? size(approxSize.usdcValue, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
            : fmt.text('0.00', { sensitive: true })
        return { approxSizeText: `${usdc} USDC (${contracts})`, approxSizeUsdSuffix: null }
      }
      // Non-quote option (e.g., BTC-4APR26-66500-C): price is in BTC → show BTC total
      // (contracts * optionPrice).
      const btc =
        approxSize.btcValue !== null && approxSize.btcValue !== undefined
          ? size(approxSize.btcValue, { maximumFractionDigits: 6, minimumFractionDigits: 2 })
          : fmt.text('0.00000', { sensitive: true })
      return {
        approxSizeText: `${btc} ${approxSize.baseTicker} (${contracts})`,
        approxSizeUsdSuffix: null,
      }
    }

    // Perpetual USD quote display
    if (approxSize.isUSDQuote && approxSize.usd !== null) {
      const usd = size(approxSize.usd, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
      return {
        approxSizeText: `$${usd} (${base} ${approxSize.baseTicker})`,
        approxSizeUsdSuffix: null,
      }
    }

    // Perpetual non-USD quote display
    const quote = size(approxSize.quote, { maximumFractionDigits: 8 })
    return {
      approxSizeText: `${quote} ${approxSize.quoteTicker} (${base} ${approxSize.baseTicker})`,
      approxSizeUsdSuffix:
        approxSize.usd !== null && approxSize.usd > 0
          ? `$${size(approxSize.usd, { maximumFractionDigits: 2 })}`
          : null,
    }
  }, [approxSize, fmt, t])

  // Get price field info for single item (quote currency for label/prefix)
  // Get price step using tickSize + tickSizeSteps (variable tick schedule)
  const priceTickSize = useMemo(() => {
    if (!isSingleItem || fields.length === 0) return undefined
    const field = fields[0]

    const instData = getFieldInstrument(field)

    if (!instData) return undefined

    const baseTickSize = Number(instData.tickSize) || 0
    if (baseTickSize <= 0) return undefined

    const steps = instData.tickSizeSteps ?? []
    if (steps.length > 0) {
      const priceNum = parseFormattedNumber(price || '0')
      // Find applicable step: walk sorted steps and apply last one where price > abovePrice
      const sortedSteps = [...steps].sort((a, b) => Number(a.abovePrice) - Number(b.abovePrice))
      let tickSize = baseTickSize
      for (const step of sortedSteps) {
        if (priceNum > Number(step.abovePrice)) {
          tickSize = Number(step.tickSize)
        }
      }
      return tickSize > 0 ? tickSize : undefined
    }

    return baseTickSize
  }, [isSingleItem, fields, getFieldInstrument, price])

  //  console.log()

  // No items selected
  if (fields.length === 0) {
    return <OrderFormEmptyState cardId={formContext.cardId} />
  }

  return (
    <div ref={formRootRef} className="mx-auto flex w-full flex-col gap-3 p-4 pt-0">
      {/* Order Rows */}
      <div
        data-tutorial={isTwapMode ? 'parent' : isLimitChaseMode ? 'rest' : undefined}
        className="mb-2 flex flex-col gap-4"
      >
        {groupedVisibleFields.map(({ field, index, memberIndexes }, visibleIndex) => {
          const exchange = parseExchangeField(field.exchange, primaryExchange)
          const previous = groupedVisibleFields[visibleIndex - 1]
          const startsAccountGroup =
            !previous ||
            parseExchangeField(previous.field.exchange, primaryExchange) !== exchange ||
            previous.field.accountId !== field.accountId
          const allExchangeAccounts = accountsByExchange.get(exchange) ?? []
          const isOption = field.itemType === 'option'
          const optionIndex = isOption ? getSelectedOptionIndex(index, field) : -1
          const instrumentIndex = !isOption ? getSelectedInstrumentIndex(index, field) : -1
          // Always resolve from instruments map first, fall back to symbol parse
          // (fall back to field values only for options). `instrumentMap` alone is
          // scoped to the card's own primary exchange — a row on a different
          // exchange would silently mis-resolve via the symbol-parse
          // fallback (e.g. reading the wrong hyphen-segment as the base ticker), so
          // pass this row's own exchange-scoped instrument (getFieldInstrument,
          // same fix already applied to qtyStep below) as the lookup hint.
          const fieldInstrument = getFieldInstrument(field)
          const { baseTicker, quoteCurrency, lastPrice, defaultDenomination } =
            resolveRowUnits(field)
          const priceNum = lastPrice ? safeParseFloat(lastPrice) : 0

          // Get option data by matching symbol (instrumentName)
          const option: SelectedOption | undefined = isOption
            ? selectedOptions.find((opt) => opt.instrumentName === field.symbol)
            : undefined

          // Get USD price for quote currency (1 for USD/stablecoins, market price for BTC/ETH/etc)
          const quotePriceInUSD = legQuoteUsd[quoteCurrency] ?? 1

          const tickers: TickerInfo[] = [
            { label: quoteCurrency, priceInUSD: quotePriceInUSD },
            // 0 while the leg's price is unknown — never a stand-in 1, which converted
            // "$10" into "10 BTC" on the unit toggle (InputWithTickerList refuses to convert
            // at a 0 price instead).
            { label: baseTicker, priceInUSD: priceNum * quotePriceInUSD },
          ]

          // Always use per-item amount (no global/single-item split)
          const value = form.watch(`items.${index}.amount`) || ''

          // Qty step from instrument's qtyPrecision — resolved from this field's own
          // exchange-scoped instrument list (getFieldInstrument, keyed by
          // exchange+symbol), not the active account's, so a row on a different
          // exchange gets its own exchange's real precision instead of a (possibly
          // wrong) borrowed one. qtyPrecision is a literal quantity increment (e.g.
          // "0.1"), not a decimal-place count — see resolveQtyStep.
          const qtyStep: number | undefined = (() => {
            const pp = getFieldInstrument(field)?.qtyPrecision ?? field.qtyPrecision
            const step = resolveQtyStep(pp)
            return pp !== undefined && pp !== '' && step > 0 ? step : undefined
          })()

          // The unit comes from resolveRowUnits (also feeds the min-notional check): the row's own
          // amountUnit is the one source of truth — the same value the preview and the submitted
          // order read — falling back to the preference-driven default.
          const selectedTicker = effectiveAmountUnit(
            form.watch(`items.${index}.amountUnit`) || undefined,
            tickers.map((t) => t.label),
            defaultDenomination
          )
          const rawAmountUnit = form.watch(`items.${index}.amountUnit`) || defaultDenomination

          // Convert qtyStep (already a real minimum increment, e.g. 0.00001) to the
          // currently selected denomination.
          // Inverse (BTC-PERPETUAL): qtyPrecision's decimal places are native to quote (USD)
          //   → base selected: divide by price, floor to 1 sig digit  (1 USD / 80000 → 0.00001 BTC)
          //   → quote selected: use as-is
          // Linear (BTC_USDC-PERPETUAL): qtyPrecision's decimal places are native to base (BTC)
          //   → quote selected: multiply by price, round to 2dp  (0.00001 BTC × 80000 → 0.8 USDC)
          //   → base selected: use as-is
          const effectiveQtyStep = (() => {
            if (!qtyStep || isOption || priceNum <= 0) return qtyStep
            const inv = isInverseInstrument(field.symbol, getFieldInstrument(field))
            if (inv) {
              if (selectedTicker !== baseTicker) return qtyStep // quote denomination: native
              const rawBaseStep = qtyStep / priceNum
              if (rawBaseStep <= 0) return qtyStep
              return Math.pow(10, Math.floor(Math.log10(rawBaseStep)))
            } else {
              if (selectedTicker !== quoteCurrency) return qtyStep // base denomination: native
              const quoteStep = qtyStep * priceNum
              return Math.round(quoteStep * 100) / 100 || qtyStep
            }
          })()

          // Current/max-size hints below must match the input's own real
          // precision (effectiveQtyStep) — the hardcoded 2dp default
          // formatCompactNumber otherwise falls back to truncated small
          // base-denominated amounts (e.g. "0.003 ETH" shown as "0.00").
          const sizeHintPrecision = getPrecisionFromStep(effectiveQtyStep, 2)

          // Every member must share the same amountUnit, not just the
          // representative — amount is shared across a split row (confirmed
          // policy), but a shared numeric amount only means the same thing
          // across all members if they interpret it in the SAME unit.
          // Leaving one member on a stale amountUnit while the group's
          // shared `amount` stays numerically identical would silently
          // submit that member with a wildly different actual size (e.g.
          // "10" meaning 10 BTC for one account and 10 USD for another).
          const handleTickerChange = (ticker: string) => {
            // A per-row pick: the global preference stays as it is (resetting it would re-apply
            // the preference to every row and undo this pick).
            markOrderFormManual(formContext.cardId)
            memberIndexes.forEach((memberIndex) => {
              form.setValue(`items.${memberIndex}.amountUnit`, ticker, { shouldDirty: true })
            })
          }

          // Changing the ticker on a merged card must update EVERY member
          // together (else the group would immediately split back apart the
          // instant one member's symbol diverges from the rest) — each
          // member converts independently below, preserving ITS OWN
          // accountId/exchange (not just the representative's).
          const handleSymbolChange = (newSymbol: string) => {
            const isNewSymbolOption =
              resolveOptionInstrument(newSymbol, instrumentMap.get(newSymbol)) !== null

            setSelection((prev) => {
              let nextOptions = [...prev.selectedOptions]
              let nextInstruments = [...prev.selectedInstruments]

              for (const memberIndex of memberIndexes) {
                const memberField = fields[memberIndex]
                if (!memberField) continue
                const memberIsOption = memberField.itemType === 'option'

                if (memberIsOption) {
                  const optIdx = nextOptions.findIndex(
                    (opt) =>
                      opt.instrumentName === memberField.symbol &&
                      (opt.accountId ?? '') === (memberField.accountId ?? '')
                  )
                  if (isNewSymbolOption) {
                    // Option → Option: just update instrumentName
                    if (optIdx >= 0) {
                      nextOptions[optIdx] = lockAfterEdit(nextOptions[optIdx], {
                        ...nextOptions[optIdx],
                        instrumentName: newSymbol,
                        price: '-',
                      })
                    }
                  } else {
                    // Option → Non-option: remove from options, add to instruments.
                    // Carry over THIS MEMBER'S OWN accountId/exchange — the
                    // row's account identity must survive a type conversion, or
                    // a leg silently loses its accountId (see row-group-key.ts's
                    // identity contract, which relies on every item resolving
                    // one).
                    const tradeSide = optIdx >= 0 ? nextOptions[optIdx].tradeSide : 'buy'
                    const accountId = optIdx >= 0 ? nextOptions[optIdx].accountId : undefined
                    // Carry the ORIGINAL row's own binding too — a scoped row (bound to a
                    // position/order) must stay scoped across a symbol-type conversion, not
                    // silently become retargetable.
                    const accountBinding =
                      optIdx >= 0
                        ? nextOptions[optIdx].accountBinding
                        : { kind: 'inherited' as const }
                    const memberExchange = optIdx >= 0 ? nextOptions[optIdx].exchange : undefined
                    if (optIdx >= 0) nextOptions.splice(optIdx, 1)
                    const newCatalogInstrument = instrumentMap.get(newSymbol)
                    const kind = newCatalogInstrument
                      ? getInstrumentUnifiedKind(newCatalogInstrument)
                      : newSymbol.toLowerCase().includes('-perp')
                        ? 'perp'
                        : /\d{1,2}[A-Z]{3}\d{2}/i.test(newSymbol)
                          ? 'future'
                          : 'spot'
                    nextInstruments.push({
                      symbol: newSymbol,
                      baseCurrency: resolveBaseCurrency(instrumentMap, newSymbol),
                      quoteCurrency: resolveQuoteCurrency(instrumentMap, newSymbol),
                      instrumentKind: kind,
                      side: tradeSide,
                      lastPrice: '-',
                      isAutoFromChart: true,
                      accountId,
                      accountBinding,
                      exchange: memberExchange,
                    })
                  }
                } else {
                  const instIdx = nextInstruments.findIndex(
                    (inst) =>
                      inst.symbol === memberField.symbol &&
                      (inst.accountId ?? '') === (memberField.accountId ?? '')
                  )
                  if (isNewSymbolOption) {
                    // Instrument → Option: remove from instruments, add to
                    // options. Carry over THIS MEMBER'S OWN accountId/exchange
                    // — same reasoning as the Option → Non-option branch above.
                    const tradeSide: 'buy' | 'sell' =
                      instIdx >= 0 ? (nextInstruments[instIdx].side as 'buy' | 'sell') : 'buy'
                    const accountId = instIdx >= 0 ? nextInstruments[instIdx].accountId : undefined
                    // Same carryover as the Option → Non-option branch above.
                    const accountBinding =
                      instIdx >= 0
                        ? nextInstruments[instIdx].accountBinding
                        : { kind: 'inherited' as const }
                    const memberExchange =
                      instIdx >= 0 ? nextInstruments[instIdx].exchange : undefined
                    if (instIdx >= 0) nextInstruments.splice(instIdx, 1)
                    nextOptions.push({
                      instrumentName: newSymbol,
                      tradeSide,
                      side: 'ask',
                      price: '-',
                      accountId,
                      accountBinding,
                      exchange: memberExchange,
                    })
                  } else if (instIdx >= 0) {
                    // instrumentKind (and base/quote) must be re-derived from the NEW
                    // symbol, not carried over — a kind-dropdown switch (Perp -> Spot)
                    // changes `newSymbol` to a different instrument, and leaving the old
                    // instrumentKind in place left the row's own header (order-row.tsx's
                    // `kind`, which trusts this field over its own catalog lookup)
                    // stuck showing the pre-switch kind even though the symbol/chart
                    // had already moved to the new one.
                    const newCatalogInstrument = instrumentMap.get(newSymbol)
                    const newInstrumentKind = newCatalogInstrument
                      ? getInstrumentUnifiedKind(newCatalogInstrument)
                      : newSymbol.toLowerCase().includes('-perp')
                        ? 'perp'
                        : /\d{1,2}[A-Z]{3}\d{2}/i.test(newSymbol)
                          ? 'future'
                          : 'spot'
                    nextInstruments[instIdx] = lockAfterEdit(nextInstruments[instIdx], {
                      ...nextInstruments[instIdx],
                      symbol: newSymbol,
                      baseCurrency: resolveBaseCurrency(
                        instrumentMap,
                        newSymbol,
                        newCatalogInstrument
                      ),
                      quoteCurrency: resolveQuoteCurrency(
                        instrumentMap,
                        newSymbol,
                        newCatalogInstrument
                      ),
                      instrumentKind: newInstrumentKind,
                      lastPrice: '-',
                      isAutoFromChart: true,
                    })
                    nextInstruments = nextInstruments.map((inst, i) =>
                      i !== instIdx && inst.isAutoFromChart
                        ? { ...inst, isAutoFromChart: false }
                        : inst
                    )
                  }
                }
              }

              return {
                ...prev,
                selectedOptions: nextOptions,
                selectedInstruments: nextInstruments,
              }
            })

            // The chart's tabs are deliberately left alone: the Order Form and other widgets'
            // tabs are independent in both directions.
          }

          // Opens below the venue's minimum notional: the reason shows in the row's error slot;
          // order-form-summary blocks submit from the same minNotionalByIndex entry.
          const minNotionalCheck = minNotionalByIndex[index]
          const minNotionalError = minNotionalCheck?.below
            ? t('belowMinNotional', { min: minNotionalCheck.min ?? '', quote: quoteCurrency })
            : undefined

          // minTradeAmount validation for option rows
          /*
          const minTradeAmountError = (() => {
            if (!isOption) return undefined
            const instData = instrumentListData?.instruments.find((i) => i.symbol === field.symbol)
            const minAmt = instData?.minTradeAmount ? Number(instData.minTradeAmount) : 0
            if (minAmt <= 0) return undefined
            const valueNum = parseFormattedNumber(value || '0')
            if (valueNum > 0 && valueNum < minAmt) {
              return `Min. trade amount: ${instData!.minTradeAmount}`
            }
            return undefined
          })()
          */

          // Cross-row-aware max-size hint from the combined batch preview
          // (Task 17/18): batchRowIndexByItem re-maps EACH MEMBER's own index
          // to its row in batchPreview.rows since a row earlier in the form
          // that's still mid-typing gets dropped from the batch request,
          // shifting every later row's index — see mapItemsToBatchRowIndex.
          // For a split multi-account row, the hint must be the MOST
          // RESTRICTIVE max across every selected account, not just the
          // representative member's — each account's own margin/leverage
          // independently caps how big this shared-amount row can go.
          const maxSize = (() => {
            // Same source/hint pair as baseTicker/quoteCurrency above: instrumentMap
            // (global, cross-exchange) with this row's own exchange-scoped instrument
            // (fieldInstrument) taking precedence, so a same-named instrument on
            // another exchange can't silently supply the wrong denomination.
            const denomination = resolveDenomination(
              instrumentMap,
              field.symbol,
              field.itemType,
              rawAmountUnit,
              fieldInstrument
            )
            const isQuoteSelected = denomination === Denomination.DENOMINATION_QUOTE

            let minBase: number | undefined
            let minBaseAmount: string | undefined
            let minQuoteAmount: string | undefined
            for (const memberIndex of memberIndexes) {
              const rowIndex = batchRowIndexByItem?.[memberIndex]
              const result = rowIndex !== undefined ? batchPreview?.rows[rowIndex] : undefined
              if (!result?.ok) continue
              const response = result.ok
              const split = splitMaxSize(
                response.maxSize,
                denomination,
                response.orderValueBase,
                response.orderValueQuote
              )
              if (!split.base) continue
              const baseNum = Number(split.base)
              if (!Number.isFinite(baseNum)) continue
              if (minBase === undefined || baseNum < minBase) {
                minBase = baseNum
                minBaseAmount = split.base
                minQuoteAmount = split.quote ?? undefined
              }
            }
            if (minBase === undefined || minBaseAmount === undefined) return undefined
            // Mirror the currently selected denomination (InputWithTickerList's
            // dropdown) rather than always showing the base leg — splitMaxSize
            // already computes both legs, so switching to quote only needs to
            // pick the other one instead of silently keeping the base figure.
            if (isQuoteSelected && minQuoteAmount !== undefined) {
              const quoteNum = Number(minQuoteAmount)
              if (Number.isFinite(quoteNum)) {
                return {
                  amount: minQuoteAmount,
                  displayAmount: formatCompactNumber(quoteNum, sizeHintPrecision),
                  ticker: quoteCurrency,
                }
              }
            }
            return {
              amount: minBaseAmount,
              displayAmount: formatCompactNumber(minBase, sizeHintPrecision),
              ticker: baseTicker,
            }
          })()

          const currentSize = (() => {
            // Mirror the currently selected denomination the same way maxSize
            // does — convert the stored (base- or quote-denominated) amount
            // to whichever ticker InputWithTickerList's dropdown has selected,
            // instead of always showing whatever unit the source happened to
            // store it in. Options are always base-denominated (contracts, see
            // resolveDenomination) — and a coin-margined option (Deribit
            // ETH-16OCT26-2850-C) has base === quote === ETH, so a ticker
            // comparison would read "ETH" as quote and multiply the contract
            // count by the premium (77 × 0.02 → "2").
            const isQuoteSelected = !isOption && selectedTicker === quoteCurrency
            const targetTicker = isQuoteSelected ? quoteCurrency : baseTicker
            const convertToTarget = (amount: number, sourceIsQuote: boolean): number | null => {
              if (sourceIsQuote === isQuoteSelected) return amount
              if (!priceNum || priceNum <= 0) return null
              return sourceIsQuote ? amount / priceNum : amount * priceNum
            }

            const isSpot =
              field.instrumentKind?.toLowerCase() === 'spot' ||
              fieldInstrument?.kind === InstrumentKind.INSTRUMENT_KIND_SPOT

            if (isSpot && field.accountId) {
              // Balances are keyed/stored in base currency.
              const amount = balancesByAccountCurrency.get(`${field.accountId}:${baseTicker}`)
              if (amount !== undefined && amount > 0) {
                const converted = convertToTarget(amount, false)
                if (converted !== null) {
                  return {
                    amount: converted.toString(),
                    displayAmount: formatCompactNumber(converted, sizeHintPrecision),
                    ticker: targetTicker,
                  }
                }
              }
            }

            if (!field.accountId) return undefined
            const position = positionsByRowKey.get(
              getPositionRowKey(field.accountId, exchange, field.symbol)
            )
            if (!position) return undefined
            const amount = Math.abs(safeParseFloat(position.quantity))
            if (!Number.isFinite(amount) || amount <= 0) return undefined

            const sourceIsQuote = position.sizeDenomination === Denomination.DENOMINATION_QUOTE
            if (sourceIsQuote === isQuoteSelected) {
              const exactAmount = position.quantity.startsWith('-')
                ? position.quantity.slice(1)
                : position.quantity
              return {
                amount: exactAmount,
                displayAmount: formatCompactNumber(amount, sizeHintPrecision),
                ticker: targetTicker,
              }
            }

            const converted = convertToTarget(amount, sourceIsQuote)
            if (converted === null) return undefined
            return {
              amount: converted.toString(),
              displayAmount: formatCompactNumber(converted, sizeHintPrecision),
              ticker: targetTicker,
            }
          })()

          return (
            <Fragment key={field.fieldId}>
              {/* Exchange icon+label + the account that owns the rows below.
                  Repeating this header for each account keeps Deribit Personal
                  and Deribit Spam visibly separate. Same shared
                  bar as order-form-summary.tsx's account-preview strip, so
                  the two read as one consistent pattern rather than two
                  independently-tuned looks. */}
              {startsAccountGroup && (
                // -mx-4 bleeds this bar out past formRootRef's own p-4 so its
                // bg-foreground/5 background reaches the true left/right
                // edges — matching order-form-summary.tsx's account-preview
                // bar, whose own wrapping container carries no horizontal
                // padding at all. The row content below stays inset as
                // normal (OrderRow is a separate sibling, still governed by
                // formRootRef's padding).
                <div className="border-foreground/5 -mx-4 border-t pt-2 first:border-t-0 first:pt-0">
                  <ExchangeAccountTabBar
                    exchange={exchange}
                    accounts={allExchangeAccounts}
                    activeAccountId={field.accountId ?? resolveInheritedAccountId(exchange)}
                    onSelectAccount={(accountId) =>
                      setOrderAccountFilter(exchange, accountId, field.accountId)
                    }
                    plainInactiveTabs
                    // Every row in this exchange+account section. Goes through
                    // handleRemoveGroup like a row's own X, so the rows left
                    // behind keep their order (preserveCurrentItemOrder).
                    onRemoveAll={() =>
                      handleRemoveGroup(
                        groupedVisibleFields.flatMap((group) =>
                          parseExchangeField(group.field.exchange, primaryExchange) === exchange &&
                          group.field.accountId === field.accountId
                            ? group.memberIndexes
                            : []
                        )
                      )
                    }
                    removeAllLabel={t('removeAccountOrderRows')}
                  />
                </div>
              )}
              <PromptFillFlash
                cardId={formContext.cardId}
                area={{ kind: 'leg', symbol: field.symbol }}
              >
                <OrderRow
                  cardId={formContext.cardId}
                  mode={isOption ? 'option' : 'perp'}
                  side={(form.watch(`items.${index}.side`) as 'buy' | 'sell') || field.side}
                  onSideChange={(newSide) => handleSideChange(index, newSide)}
                  value={value}
                  onValueChange={(val) => handleAmountChangeForGroup(memberIndexes, val)}
                  tickers={tickers}
                  selectedTicker={selectedTicker}
                  onTickerChange={handleTickerChange}
                  onSymbolChange={handleSymbolChange}
                  symbol={field.symbol}
                  lastPrice={lastPrice}
                  option={option}
                  instrumentKind={field.instrumentKind}
                  baseCurrency={field.baseCurrency}
                  quoteCurrency={field.quoteCurrency}
                  exchange={field.exchange}
                  accountId={resolveItemAccountId(field, exchange)}
                  index={index}
                  optionIndex={optionIndex >= 0 ? optionIndex : undefined}
                  autoFocus={index === firstEmptyAmountIndex}
                  amountInputIndex={index}
                  onRemove={() => handleRemoveGroup(memberIndexes)}
                  qtyStep={effectiveQtyStep}
                  currentSize={currentSize}
                  maxSize={maxSize}
                  lockedAmount={(watchedItems?.[index] ?? field).lockedAmount}
                  minTradeAmountError={minNotionalError}
                  spreaderRole={
                    isSpreaderMode
                      ? ((form.watch(`items.${index}.spreaderRole`) as 'quoting' | 'taking') ??
                        'taking')
                      : undefined
                  }
                  onSpreaderRoleChange={
                    isSpreaderMode ? (role) => handleSpreaderRoleChange(index, role) : undefined
                  }
                />
              </PromptFillFlash>
            </Fragment>
          )
        })}
        {groupedVisibleFields.length === 0 && (
          <p className="text-muted-foreground py-4 text-center text-sm">{t('noRowsForAccount')}</p>
        )}
      </div>

      {/* Spreader: build from an empty ticket or append beyond a matrix-seeded pair.
          The first leg is quoting; later legs are taking by default. */}
      {isSpreaderMode && (
        <SpreaderTickerPicker
          existing={spreaderLegInstruments}
          preferredBaseCurrency={spreaderPreferredBaseCurrency}
          onSelect={addSpreaderLeg}
          trigger={
            <button
              type="button"
              className="text-foreground/50 hover:text-foreground/80 flex w-full items-center justify-center gap-1 border border-dashed border-white/15 py-1.5 text-xs"
            >
              <PlusIcon className="size-3" /> Add leg
            </button>
          }
        />
      )}

      {/* Price / Anchor Field - shown for all limit orders (single or multi-instrument) */}
      {!isMarketOrder &&
        !isLimitChaseMode &&
        !isTwapMode &&
        !isAdvancedTwapMode &&
        !isScaleMode &&
        !isRfqMode &&
        !isSpreaderMode &&
        fields.length > 0 && (
          <div className="flex flex-col gap-3">
            <PromptFillFlash cardId={formContext.cardId} area={{ kind: 'price' }}>
              <LimitPriceField
                anchor={priceAnchor}
                onAnchorChange={(a) => {
                  // A deliberate pick, whatever the value — no longer the auto-switch's to revert.
                  autoSwitchedAnchorRef.current = false
                  form.setValue('priceAnchor', a, { shouldDirty: true })
                }}
                offsetUnit={priceOffsetUnit}
                onOffsetUnitChange={(u) =>
                  form.setValue('priceOffsetUnit', u, { shouldDirty: true })
                }
                value={price || ''}
                onValueChange={(val) => {
                  form.setValue('price', val, { shouldDirty: true })
                  markPriceSynced()
                }}
                onReset={() => {
                  form.setValue('price', '0', { shouldDirty: true })
                  markPriceSynced()
                }}
                onSwitchToPrice={() => {
                  syncPriceFromAtm()
                }}
                step={isSingleItem ? priceTickSize : undefined}
                quoteCurrency={quoteCurrencyLabel}
                disableAbsolutePrice={!isSingleItem}
                disableQuoteValueUnit={!isSingleItem}
                tickerData={isSingleItem ? legTickerMap[fields[0].symbol] : undefined}
                tickSize={isSingleItem ? priceTickSize : undefined}
              />
            </PromptFillFlash>

            {priceWarnings}

            {/* Approximate size info - single item only */}
            {approxSize && isSingleItem && (
              <div className="text-foreground/40 mr-1 ml-auto w-fit text-xs">
                <p>
                  {t('approxSize', { size: approxSizeText })}
                  {approxSizeUsdSuffix && (
                    <span className="text-foreground/30"> ≈ {approxSizeUsdSuffix}</span>
                  )}
                </p>
              </div>
            )}
          </div>
        )}

      {!isRfqMode && !isSpreaderMode && <OrderExecutionParams />}

      {/* RFQ: makers, disclosure, manual/auto, optional hedge (legs come from the rows above) */}
      {isRfqMode && <RfqParams cardId={formContext.cardId} />}

      {/* Spreader: slice size + optional limit price for the quoting leg's chase. Per-leg
          role (Quoting/Taking) and side live on each row above instead — see order-row.tsx. */}
      {isSpreaderMode && <SpreaderParams />}

      {/* TWAP: order duration */}
      {isTwapMode && (
        <div data-tutorial={isMultiLeg ? undefined : 'slices'} className="flex flex-col gap-3">
          <TwapDurationField label={t('duration')} />
        </div>
      )}

      {/* Multi-leg TWAP Info - only show in TWAP mode */}
      {isMultiLeg && isTwapMode && (
        <div data-tutorial="slices" className="bg-white/5 p-3">
          <div className="flex items-center gap-2">
            <div className="bg-chart-mint size-2 animate-pulse rounded-full" />
            <span className="text-foreground/60 text-sm font-medium">{t('twapExecution')}</span>
          </div>
          <p className="text-foreground/40 mt-2 text-xs">{t('twapExecutionDescription')}</p>
        </div>
      )}

      {/* Advanced TWAP: duration/slices, price band, catchup cadence, reduce-only. Single
          instrument/account only — a multi-leg or split-account row can't be built into one
          AdvancedTwapAlgoParams, so isFormValid (order-form-summary.tsx) disables submit
          in that case rather than this section trying to render per-row controls. */}
      {isAdvancedTwapMode && <AdvancedTwapParams />}
      {isScaleMode && <ScaleParams />}
    </div>
  )
}
